
import { Lead, LeadStatus, Activity } from '@/types/lead';
import { LeadScoreBreakdown, ScoreFactor } from '@/types/leadScore';

// Calculate a detailed score breakdown
export const calculateScoreBreakdown = (lead: Lead, activities: Activity[]): LeadScoreBreakdown => {
  // Demographic scoring: Based on available contact information
  const demographicScore = calculateDemographicScore(lead);
  
  // Engagement scoring: Based on status and interactions
  const engagementScore = calculateEngagementScore(lead);
  
  // Activity scoring: Based on notes, documents, and interactions
  const activityScore = calculateActivityScore(lead, activities);
  
  // Recency scoring: How recently was there activity with this lead
  const recencyScore = calculateRecencyScore(lead, activities);
  
  // Total score is the sum of all component scores
  const totalScore = demographicScore.score + engagementScore.score + 
                    activityScore.score + recencyScore.score;
  
  return {
    totalScore,
    scoreDetails: {
      demographic: demographicScore,
      engagement: engagementScore,
      activity: activityScore,
      recency: recencyScore
    }
  };
};

// Helper functions for each score component
export const calculateDemographicScore = (lead: Lead) => {
  let score = 0;
  const factors: ScoreFactor[] = [];
  
  // Completeness of profile
  if (lead.name) {
    score += 5;
    factors.push({ rule: 'Has business name', points: 5, description: 'Business name is provided' });
  }
  
  if (lead.email) {
    score += 10;
    factors.push({ rule: 'Has email', points: 10, description: 'Email contact is available' });
  }
  
  if (lead.phone) {
    score += 10;
    factors.push({ rule: 'Has phone', points: 10, description: 'Phone contact is available' });
  }
  
  if (lead.website) {
    score += 5;
    factors.push({ rule: 'Has website', points: 5, description: 'Website is available' });
  }
  
  if (lead.address) {
    score += 5;
    factors.push({ rule: 'Has address', points: 5, description: 'Physical address is available' });
  }
  
  // Check for key contacts
  if (lead.keyContacts && lead.keyContacts.length > 0) {
    const points = Math.min(lead.keyContacts.length * 5, 15);
    score += points;
    factors.push({ 
      rule: 'Has key contacts', 
      points, 
      description: `${lead.keyContacts.length} key contacts identified` 
    });
  }
  
  return { score, factors };
};

export const calculateEngagementScore = (lead: Lead) => {
  let score = 0;
  const factors: ScoreFactor[] = [];
  
  // Score based on lead status
  const statusScores: Record<LeadStatus, number> = {
    'new': 5,
    'contacted': 15,
    'qualified': 30,
    'proposal': 40,
    'negotiation': 50,
    'closed_won': 60,
    'closed_lost': 0
  };
  
  if (lead.status) {
    const statusScore = statusScores[lead.status as LeadStatus] || 0;
    score += statusScore;
    factors.push({ 
      rule: 'Status progression', 
      points: statusScore, 
      description: `Lead is in ${lead.status} status` 
    });
  }
  
  // Favorited leads get extra points
  if (lead.isFavorite) {
    score += 10;
    factors.push({ rule: 'Favorited lead', points: 10, description: 'Lead has been marked as favorite' });
  }
  
  // Rating bonus if available (e.g., from Google Places)
  if (lead.rating && lead.rating > 0) {
    const ratingScore = Math.round(lead.rating * 2);
    score += ratingScore;
    factors.push({ 
      rule: 'External rating', 
      points: ratingScore, 
      description: `Business has ${lead.rating} star rating` 
    });
  }
  
  return { score, factors };
};

export const calculateActivityScore = (lead: Lead, activities: Activity[]) => {
  let score = 0;
  const factors: ScoreFactor[] = [];
  
  // Score for having notes
  if ((lead.notes || []).length > 0) {
    const noteScore = Math.min((lead.notes || []).length * 3, 15);
    score += noteScore;
    factors.push({ 
      rule: 'Has notes', 
      points: noteScore, 
      description: `${(lead.notes || []).length} notes recorded` 
    });
  }
  
  // Score for having documents
  if ((lead.documents || []).length > 0) {
    const docScore = Math.min((lead.documents || []).length * 5, 15);
    score += docScore;
    factors.push({ 
      rule: 'Has documents', 
      points: docScore, 
      description: `${(lead.documents || []).length} documents attached` 
    });
  }
  
  // Score for activity types
  const activityCounts: Record<string, number> = {};
  (activities || []).forEach(activity => {
    activityCounts[activity.type] = (activityCounts[activity.type] || 0) + 1;
  });
  
  // Score for communication activities
  if (activityCounts['communication']) {
    const commScore = Math.min(activityCounts['communication'] * 5, 20);
    score += commScore;
    factors.push({ 
      rule: 'Communication activities', 
      points: commScore, 
      description: `${activityCounts['communication']} communication records` 
    });
  }
  
  // Score for status changes
  if (activityCounts['status_change']) {
    const statusChangeScore = Math.min(activityCounts['status_change'] * 3, 15);
    score += statusChangeScore;
    factors.push({ 
      rule: 'Status progression', 
      points: statusChangeScore, 
      description: `${activityCounts['status_change']} status changes recorded` 
    });
  }
  
  return { score, factors };
};

export const calculateRecencyScore = (lead: Lead, activities: Activity[]) => {
  let score = 0;
  const factors: ScoreFactor[] = [];
  
  // Determine the most recent activity date
  let mostRecentDate = lead.createdAt;
  
  if (activities && activities.length > 0) {
    const activityDates = activities.map(a => new Date(a.createdAt).getTime());
    mostRecentDate = new Date(Math.max(...activityDates)).toISOString();
  }
  
  // Calculate days since last activity
  const daysSinceActivity = Math.floor(
    (new Date().getTime() - new Date(mostRecentDate).getTime()) / (1000 * 60 * 60 * 24)
  );
  
  // Score based on recency
  if (daysSinceActivity <= 1) {
    score += 30;
    factors.push({ rule: 'Very recent activity', points: 30, description: 'Activity within last day' });
  } else if (daysSinceActivity <= 7) {
    score += 20;
    factors.push({ rule: 'Recent activity', points: 20, description: 'Activity within last week' });
  } else if (daysSinceActivity <= 30) {
    score += 10;
    factors.push({ rule: 'Moderate recency', points: 10, description: 'Activity within last month' });
  } else if (daysSinceActivity <= 90) {
    score += 5;
    factors.push({ rule: 'Low recency', points: 5, description: 'Activity within last quarter' });
  } else {
    factors.push({ rule: 'Inactive', points: 0, description: 'No recent activity' });
  }
  
  return { score, factors };
};
